// services/api.js
// Propósito: Encapsular llamadas al backend y manejo de errores.
// Por qué: Centraliza la comunicación y prepara para futuras configuraciones.

// Importar labsAPI
export { labsAPI } from './labsAPI.js';

const env = (typeof import.meta !== 'undefined' && import.meta.env) || {};
const processEnv = (typeof process !== 'undefined' && process.env) || {};

// Detectar si estamos en producción
const isProduction = typeof window !== 'undefined' && 
                     window.location.hostname !== 'localhost' && 
                     window.location.hostname !== '127.0.0.1';

// Fallback explícito para producción (Render)
const PROD_API_BASE = 'https://backend-url-security-analyzer.onrender.com';

// Configurar API_BASE con prioridad a variables de entorno
let API_BASE = env.VITE_API_BASE_URL ||
               env.VITE_API_URL ||
               processEnv.REACT_APP_API_BASE_URL || 
               processEnv.REACT_APP_API_URL ||
               processEnv.API_BASE_URL || 
               (isProduction ? PROD_API_BASE : null) ||
               (isProduction 
                 ? (window.location.protocol + '//' + window.location.host.replace('frontend-', 'backend-'))
                 : 'http://localhost:5000');

// Evitar apuntar al dominio del frontend en producción
if (isProduction) {
  const frontendHost = window.location.hostname;
  const looksLikeFrontend = API_BASE?.includes(frontendHost);
  if (looksLikeFrontend) {
    API_BASE = PROD_API_BASE;
  }
}

console.log('🔧 API_BASE:', API_BASE);
console.log('🌐 Environment:', isProduction ? 'production' : 'development');

export async function analyzeUrl(url) {
  try {
    const res = await fetch(`${API_BASE}/api/analyze`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Error del servidor (${res.status}): ${text || res.statusText}`);
    }
    return await res.json();
  } catch (err) {
    if (err.name === 'TypeError') {
      // Posible backend caído o CORS
      throw new Error('No se pudo conectar al backend. Verifique que esté ejecutándose.');
    }
    throw err;
  }
}

// Auth endpoints
export async function login(email, password) {
  const res = await fetch(`${API_BASE}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || 'No se pudo iniciar sesión');
  }
  return data; // { user, token }
}

export async function register(name, email, password) {
  const res = await fetch(`${API_BASE}/api/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, email, password })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || 'No se pudo registrar');
  }
  return data; // { user, token }
}

export async function getMe(token) {
  const res = await fetch(`${API_BASE}/api/auth/me`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || 'No se pudo obtener el usuario');
  }
  return data; // { user }
}

// Tools: Enumeration
export async function enumerate(baseUrl, token) {
  const res = await fetch(`${API_BASE}/api/tools/enumerate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ baseUrl })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || 'No se pudo enumerar URLs');
  }
  return data; // { baseUrl, results: [] }
}

// Email analysis (requiere autenticación)
export async function analyzeEmails(emails, token) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const res = await fetch(`${API_BASE}/api/email/analyze`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ emails })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || 'No se pudo analizar emails');
  }
  return data;
}

// Perfil de usuario (protegido)
export async function getProfile(token) {
  const res = await fetch(`${API_BASE}/api/user/profile`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || 'No se pudo obtener el perfil');
  }
  return data;
}

export async function updateProfile({ name, email, phone }, token) {
  const res = await fetch(`${API_BASE}/api/user/profile`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ name, email, phone })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || 'No se pudo actualizar el perfil');
  }
  return data; // { user }
}

export async function changePassword({ oldPassword, newPassword }, token) {
  const res = await fetch(`${API_BASE}/api/user/password`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ oldPassword, newPassword })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || 'No se pudo cambiar la contraseña');
  }
  return data; // { success: true }
}
